import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { DailySunlightResult } from '../types/sunlight';
import { Forma } from 'forma-embedded-view-sdk/auto';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { clearAllSelections } from './gridSelection';
import { Clipper, EndType, JoinType, Paths64 } from 'clipper2-ts';

export const METRICS_CONFIG: Record<string, { max: number; inverse?: boolean }> = {
	averageDailySunlightHours: { max: 12 },
	winterMonthsAverage: { max: 8 },
	summerMonthsAverage: { max: 14 },
	springMonthsAverage: { max: 12 },
	fallMonthsAverage: { max: 10 },
	consistencyScore: { max: 1 },
	midDayHoursAverage: { max: 4 },
	daylightAutonomyPercent: { max: 100 },
	directionalBalance: { max: 1 },
	morningHoursPercent: { max: 100 },
	afternoonHoursPercent: { max: 100 },
	longestSunlightHours: { max: 16 },
	shortestSunlightHours: { max: 12 },
	minViableDaysCount: { max: 365 },
	daysWithSunlight: { max: 365 },
	glareRiskHours: { max: 4, inverse: true },
	zeroDaysCount: { max: 100, inverse: true },
};

export const addEdgesToMesh = (mesh: THREE.Mesh, scene: THREE.Scene, color?: number) => {
	const edges = new THREE.EdgesGeometry(mesh.geometry, 30);
	const line = new THREE.LineSegments(
		edges,
		new THREE.LineBasicMaterial({
			color: color ?? 0x000000,
			linewidth: 1,
			// TODO: Some issues with clipping planes and edges
			clippingPlanes: [],
		})
	);

	line.position.copy(mesh.position);
	line.rotation.copy(mesh.rotation);
	line.scale.copy(mesh.scale);

	line.userData.isEdge = true;
	line.userData.parentMeshUuid = mesh.uuid;

	line.renderOrder = mesh.renderOrder + 1;
	scene.add(line);
};

export const removeEdgesFromMesh = (mesh: THREE.Mesh, scene: THREE.Scene) => {
	const edgesToRemove = scene.children.filter((child) => {
		return child.userData.isEdge && child.userData.parentMeshUuid === mesh.uuid;
	});

	edgesToRemove.forEach((edge) => {
		scene.remove(edge);
	});
};

/**
 * Merges all individual per-mesh edge LineSegments (isEdge = true) in the scene into a single
 * LineSegments draw call. Call once after scene generation is complete.
 * The resulting object has userData.isAllBuildingEdges = true for bulk visibility toggling.
 */
export const mergeBuildingEdges = (scene: THREE.Scene): void => {
	// Collect all individual edge objects that haven't been merged yet
	const edgeObjects: THREE.LineSegments[] = [];
	scene.traverse((child) => {
		if (
			child instanceof THREE.LineSegments &&
			child.userData.isEdge === true &&
			!child.userData.isMergedEdges
		) {
			edgeObjects.push(child);
		}
	});

	if (edgeObjects.length === 0) return;

	// Apply each object's world transform into geometry positions so merge is correct
	const worldGeometries: THREE.BufferGeometry[] = edgeObjects.map((obj) => {
		obj.updateMatrixWorld(true);
		const geo = obj.geometry.clone();
		geo.applyMatrix4(obj.matrixWorld);
		return geo;
	});

	const merged = mergeGeometries(worldGeometries, false);

	// Dispose temporary clones
	worldGeometries.forEach((g) => g.dispose());

	if (!merged) return;

	const mergedLine = new THREE.LineSegments(
		merged,
		new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 1 })
	);
	mergedLine.name = 'MergedBuildingEdges';
	mergedLine.userData.isEdge = true;
	mergedLine.userData.isMergedEdges = true;
	mergedLine.userData.isAllBuildingEdges = true;
	mergedLine.renderOrder = 1;

	// Remove all individual edge objects
	for (const obj of edgeObjects) {
		obj.parent?.remove(obj);
		obj.geometry.dispose();
		if (obj.material instanceof THREE.Material) obj.material.dispose();
	}

	scene.add(mergedLine);
};

/**
 * Highlights a building with blue edges to make it visually distinct (similar to Forma selection)
 */
export const highlightSelectedBuilding = (building: THREE.Object3D, scene: THREE.Scene) => {
	// Remove any existing building highlights first
	removeSelectedBuildingHighlight(scene);

	// Add blue edges to all meshes in the building hierarchy
	building.traverse((child) => {
		if (child instanceof THREE.Mesh && child.userData.category === 'building') {
			const edges = new THREE.EdgesGeometry(child.geometry, 30);
			const line = new THREE.LineSegments(
				edges,
				new THREE.LineBasicMaterial({
					color: 0x0066ff, // Bright blue like Forma
					linewidth: 1,
					transparent: true,
					opacity: 0.5,
					depthTest: true,
					clippingPlanes: [],
				})
			);

			line.position.copy(child.position);
			line.rotation.copy(child.rotation);
			line.scale.copy(child.scale);

			line.userData.isSelectedBuildingHighlight = true;
			line.userData.parentMeshUuid = child.uuid;

			line.renderOrder = child.renderOrder + 2; // Render on top
			scene.add(line);
		}
	});
};

/**
 * Removes building highlight edges from the scene
 */
export const removeSelectedBuildingHighlight = (scene: THREE.Scene) => {
	const highlightsToRemove = scene.children.filter((child) => {
		return child.userData.isSelectedBuildingHighlight;
	});

	highlightsToRemove.forEach((highlight) => {
		scene.remove(highlight);
		if (highlight instanceof THREE.LineSegments) {
			highlight.geometry.dispose();
			if (highlight.material instanceof THREE.Material) {
				highlight.material.dispose();
			}
		}
	});
};

export const getBuildingGraph = async (building: THREE.Object3D) => {
	const { element } = await Forma.elements.getByPath({
		path: building.userData.path,
	});

	if (element.representations?.graphBuilding) {
		const graphBuilding = await Forma.elements.representations.graphBuilding(element);
		return graphBuilding;
	}
};

export const highlightCell = (roomName: string, scene: THREE.Scene) => {
	scene.traverse((obj) => {
		if (obj instanceof THREE.Mesh && obj.name === roomName) {
			if (!obj.userData.isHighlighted) {
				const material = obj.material as
					| THREE.MeshStandardMaterial
					| THREE.MeshBasicMaterial;

				obj.userData.originalOpacity = material.opacity;
				obj.userData.originalTransparent = material.transparent;

				material.transparent = true;
				material.opacity = 0.8;
				material.needsUpdate = true;

				obj.userData.isHighlighted = true;
			}
		}
	});
};

export const removeHighlight = (scene: THREE.Scene) => {
	scene.traverse((obj) => {
		if (obj instanceof THREE.Mesh && obj.userData.isHighlighted) {
			const material = obj.material as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;

			if (obj.userData.originalOpacity !== undefined) {
				material.opacity = obj.userData.originalOpacity;
			}
			if (obj.userData.originalTransparent !== undefined) {
				material.transparent = obj.userData.originalTransparent;
			}
			material.needsUpdate = true;

			const materialWithColor = material as THREE.Material & {
				originalColor?: number;
				color: THREE.Color;
			};
			if (materialWithColor.originalColor) {
				materialWithColor.color.setHex(materialWithColor.originalColor);
			}

			obj.userData.isHighlighted = false;
			delete obj.userData.originalOpacity;
			delete obj.userData.originalTransparent;
		}
	});
};

let currentInstancedMesh: THREE.InstancedMesh | null = null;
let maxInstances = 0;
let highlightedCellsMesh: THREE.InstancedMesh | null = null;

export function initializeSunlightVisualization(
	scene: THREE.Scene,
	maxGridPoints: number,
	cellSize: number = 1.0
) {
	clearSunlightVisualization(scene);
	clearSelectedRegionVisualization(scene);
	hidePolygonOutline(scene);

	const gridGroup = new THREE.Group();
	gridGroup.name = 'SunlightGridVisualization';

	const sharedGeometry = new THREE.PlaneGeometry(cellSize * 1, cellSize * 1);

	const sharedMaterial = new THREE.MeshBasicMaterial({
		side: THREE.FrontSide,
		transparent: false,
		opacity: 1.0,
		depthWrite: false,
	});

	currentInstancedMesh = new THREE.InstancedMesh(sharedGeometry, sharedMaterial, maxGridPoints);
	currentInstancedMesh.name = 'SunlightGridInstances';
	currentInstancedMesh.userData.isRoomSunlightVisualization = true;
	currentInstancedMesh.renderOrder = 100;

	currentInstancedMesh.count = 0;

	gridGroup.add(currentInstancedMesh);
	scene.add(gridGroup);

	maxInstances = maxGridPoints;
}

export function updateSunlightVisualization(sunlightData: DailySunlightResult[]) {
	if (!currentInstancedMesh) {
		console.warn(
			'Sunlight visualization not initialized. Call initializeSunlightVisualization first.'
		);
		return;
	}

	const matrix = new THREE.Matrix4();
	const color = new THREE.Color();

	const gridPointsData: DailySunlightResult[] = [];

	let instanceIndex = 0;

	sunlightData.forEach((data) => {
		if (instanceIndex >= maxInstances) {
			console.warn('Exceeded max instances capacity');
			return;
		}

		gridPointsData[instanceIndex] = data;

		const position = data.point.clone();
		position.z += 0.01; // Offset slightly above floor
		matrix.setPosition(position);

		// Set instance transform and color
		if (currentInstancedMesh) {
			currentInstancedMesh.setMatrixAt(instanceIndex, matrix);
			currentInstancedMesh.setColorAt(instanceIndex, color);
		}

		instanceIndex++;
	});

	currentInstancedMesh.userData.gridPointsData = gridPointsData;
	currentInstancedMesh.userData.isRoomSunlightVisualization = true;

	currentInstancedMesh.count = instanceIndex;

	if (currentInstancedMesh.instanceMatrix) {
		currentInstancedMesh.instanceMatrix.needsUpdate = true;
	}
	if (currentInstancedMesh.instanceColor) {
		currentInstancedMesh.instanceColor.needsUpdate = true;
	}

	currentInstancedMesh.computeBoundingSphere();
	changeSunlightVisualizationMetric(
		sunlightData,
		useDailySunlightStore.getState().currentSelectedMetric
	);
}

let currentSelectedRegionMesh: THREE.InstancedMesh | null = null;
let maxSelectedRegionInstances = 0;

export function initializeSelectedRegionVisualization(
	scene: THREE.Scene,
	maxGridPoints: number,
	cellSize: number = 1.0
) {
	const gridGroup = new THREE.Group();
	gridGroup.name = 'SelectedRegionSunlightVisualization';

	const sharedGeometry = new THREE.PlaneGeometry(cellSize, cellSize);

	const sharedMaterial = new THREE.MeshBasicMaterial({
		side: THREE.FrontSide,
		transparent: false,
		opacity: 1.0,
		depthWrite: false,
	});

	currentSelectedRegionMesh = new THREE.InstancedMesh(
		sharedGeometry,
		sharedMaterial,
		maxGridPoints
	);
	currentSelectedRegionMesh.name = 'SelectedRegionGridInstances';
	currentSelectedRegionMesh.userData.isSelectedRegionVisualization = true;
	currentSelectedRegionMesh.userData.gridSize = cellSize;
	currentSelectedRegionMesh.renderOrder = 999; // Render above room visualization

	currentSelectedRegionMesh.count = 0;

	gridGroup.add(currentSelectedRegionMesh);
	scene.add(gridGroup);

	maxSelectedRegionInstances = maxGridPoints;
}

export function updateSelectedRegionVisualization(sunlightData: DailySunlightResult[]) {
	if (!currentSelectedRegionMesh) {
		console.warn(
			'Selected region visualization not initialized. Call initializeSelectedRegionVisualization first.'
		);
		return;
	}

	const matrix = new THREE.Matrix4();
	const color = new THREE.Color();

	const gridPointsData: DailySunlightResult[] = [];

	let instanceIndex = 0;

	sunlightData.forEach((data) => {
		if (instanceIndex >= maxSelectedRegionInstances) {
			console.warn('Exceeded max instances capacity for selected region');
			return;
		}

		if (!currentSelectedRegionMesh) {
			console.warn('Current instanced mesh not found for selected region update');
			return;
		}

		gridPointsData[instanceIndex] = data;

		const position = data.point.clone();
		position.z += 0.02; // Offset slightly above room visualization (Z-up: 0,0,1 is up)
		matrix.setPosition(position);

		currentSelectedRegionMesh.setMatrixAt(instanceIndex, matrix);
		currentSelectedRegionMesh.setColorAt(instanceIndex, color);

		instanceIndex++;
	});

	currentSelectedRegionMesh.userData.gridPointsData = gridPointsData;
	currentSelectedRegionMesh.userData.isSelectedRegionVisualization = true;
	const regionNumber = useDailySunlightStore.getState().sunlightResultsGridPoints?.length || 0;
	currentSelectedRegionMesh.userData.regionName = `Selected Region ${regionNumber}`;
	currentSelectedRegionMesh.count = instanceIndex;

	if (currentSelectedRegionMesh.instanceMatrix) {
		currentSelectedRegionMesh.instanceMatrix.needsUpdate = true;
	}
	if (currentSelectedRegionMesh.instanceColor) {
		currentSelectedRegionMesh.instanceColor.needsUpdate = true;
	}

	currentSelectedRegionMesh.computeBoundingSphere();

	changeSelectedRegionVisualizationMetric(
		sunlightData,
		useDailySunlightStore.getState().currentSelectedMetric
	);
}

export function changeSelectedRegionVisualizationMetric(
	sunlightData: DailySunlightResult[],
	metric: keyof DailySunlightResult,
	mesh?: THREE.InstancedMesh
) {
	const meshToUpdate = mesh ? mesh : currentSelectedRegionMesh;
	if (!meshToUpdate) {
		console.warn(
			'Selected region visualization not initialized. Call initializeSelectedRegionVisualization first.'
		);
		return;
	}

	const color = new THREE.Color();

	sunlightData.forEach((data, index) => {
		if (meshToUpdate && index >= meshToUpdate.count) return;

		let value = 0;
		const config = METRICS_CONFIG[metric] || { max: 10 };

		const metricValue = data[metric];
		if (typeof metricValue === 'number') {
			value = metricValue;
		} else if (typeof metricValue === 'string') {
			color.setRGB(0.5, 0.5, 0.5);
			if (meshToUpdate) {
				meshToUpdate.setColorAt(index, color);
			}
			return;
		}

		const normalizedValue = Math.min(Math.max(value / config.max, 0), 1.0);
		const colorValue = config.inverse ? 1 - normalizedValue : normalizedValue;

		// Color gradient: Red (bad) → Yellow (medium) → Green (good)
		if (colorValue < 0.5) {
			color.setRGB(1, colorValue * 2, 0);
		} else {
			color.setRGB(2 - colorValue * 2, 1, 0);
		}

		if (meshToUpdate) {
			meshToUpdate.setColorAt(index, color);
		}
	});

	if (meshToUpdate.instanceColor) {
		meshToUpdate.instanceColor.needsUpdate = true;
	}
}

export function clearSelectedRegionVisualization(scene: THREE.Scene) {
	const visualization = scene.getObjectByName('SelectedRegionSunlightVisualization');
	if (visualization) {
		scene.remove(visualization);
		visualization.traverse((child) => {
			if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) {
				child.geometry.dispose();
				if (Array.isArray(child.material)) {
					child.material.forEach((mat) => mat.dispose());
				} else {
					child.material.dispose();
				}
			}
		});
	}

	currentSelectedRegionMesh = null;
	maxSelectedRegionInstances = 0;
}

export function changeSunlightVisualizationMetric(
	sunlightData: DailySunlightResult[],
	metric: keyof DailySunlightResult
) {
	if (!currentInstancedMesh) {
		console.warn(
			'Sunlight visualization not initialized. Call initializeSunlightVisualization first.'
		);
		return;
	}

	const color = new THREE.Color();

	sunlightData.forEach((data, index) => {
		if (currentInstancedMesh && index >= currentInstancedMesh.count) return;

		let value = 0;
		const config = METRICS_CONFIG[metric] || { max: 10 };

		// Extract the value based on metric type
		const metricValue = data[metric];
		if (typeof metricValue === 'number') {
			value = metricValue;
		} else if (typeof metricValue === 'string') {
			color.setRGB(0.5, 0.5, 0.5);
			if (currentInstancedMesh) {
				currentInstancedMesh.setColorAt(index, color);
			}
			return;
		}

		const normalizedValue = Math.min(Math.max(value / config.max, 0), 1.0);
		const colorValue = config.inverse ? 1 - normalizedValue : normalizedValue;

		// Color gradient: Red (bad) → Yellow (medium) → Green (good)
		if (colorValue < 0.5) {
			// Red to Yellow
			color.setRGB(1, colorValue * 2, 0);
		} else {
			// Yellow to Green
			color.setRGB(2 - colorValue * 2, 1, 0);
		}

		if (currentInstancedMesh) {
			currentInstancedMesh.setColorAt(index, color);
		}
	});

	if (currentInstancedMesh.instanceColor) {
		currentInstancedMesh.instanceColor.needsUpdate = true;
	}
}

export function clearSunlightVisualization(scene: THREE.Scene) {
	const visualization = scene.getObjectByName('SunlightGridVisualization');
	if (visualization) {
		scene.remove(visualization);
		visualization.traverse((child) => {
			if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) {
				child.geometry.dispose();
				if (Array.isArray(child.material)) {
					child.material.forEach((mat) => mat.dispose());
				} else {
					child.material.dispose();
				}
			}
		});
	}

	currentInstancedMesh = null;
	maxInstances = 0;
}

export const createPolygonFromGridPoints = (
	gridPoints: Array<{ x: number; y: number; z: number }>,
	gridSize: number
): Array<{ x: number; y: number; z: number }> => {
	if (gridPoints.length < 3) {
		console.warn('Need at least 3 points to create a polygon');
		return [];
	}

	const uniquePoints = Array.from(
		new Map(
			gridPoints.map((point) => [
				`${point.x.toFixed(6)},${point.y.toFixed(6)},${point.z.toFixed(6)}`,
				point,
			])
		).values()
	);

	if (uniquePoints.length < 3) {
		console.warn('Need at least 3 unique points to create a polygon');
		return [];
	}

	const centroid = uniquePoints.reduce(
		(acc, point) => {
			acc.x += point.x;
			acc.y += point.y;
			acc.z += point.z;
			return acc;
		},
		{ x: 0, y: 0, z: 0 }
	);

	centroid.x /= uniquePoints.length;
	centroid.y /= uniquePoints.length;
	centroid.z /= uniquePoints.length;

	const sortedPoints = uniquePoints
		.map((point) => ({
			point,
			angle: Math.atan2(point.y - centroid.y, point.x - centroid.x),
		}))
		.sort((a, b) => a.angle - b.angle)
		.map(({ point }) => point);

	const SCALE = 1000;

	const path = sortedPoints.map((v) => ({
		x: Math.round(v.x * SCALE),
		y: Math.round(v.y * SCALE),
	}));
	const solution: Paths64 = Clipper.inflatePaths(
		[path],
		(gridSize / 2) * SCALE,
		JoinType.Miter,
		EndType.Polygon
	);

	return solution[0].map((pt) => ({ x: pt.x / SCALE, y: pt.y / SCALE, z: centroid.z }));
};

export const showPolygonOutline = (
	scene: THREE.Scene,
	polygon: Array<{ x: number; y: number; z: number }>
) => {
	if (polygon.length < 3) {
		hidePolygonOutline(scene);
		return;
	}

	const points = polygon.map((p) => new THREE.Vector3(p.x, p.y, p.z));
	points.push(points[0].clone());

	const geometry = new THREE.BufferGeometry().setFromPoints(points);
	const material = new THREE.LineBasicMaterial({
		color: 0x00ff00,
		linewidth: 3,
		transparent: true,
		opacity: 0.8,
		depthTest: false,
		depthWrite: false,
	});

	const line = new THREE.Line(geometry, material);
	line.name = 'selectedGridPolygonOutline';
	line.renderOrder = 1002; // Render on top of grid selections

	scene.add(line);
};

export const hidePolygonOutline = (scene: THREE.Scene) => {
	const existingOutline = scene.getObjectByName('selectedGridPolygonOutline');
	if (existingOutline) {
		scene.remove(existingOutline);
		existingOutline.traverse((child) => {
			if (child instanceof THREE.Line) {
				child.geometry.dispose();
				(child.material as THREE.Material).dispose();
			}
		});
	}
};

let polygonFillMesh: THREE.Mesh | null = null;
const persistedFillMeshes: THREE.Mesh[] = [];

export const showPolygonFill = (
	scene: THREE.Scene,
	polygon: Array<{ x: number; y: number; z: number }>
) => {
	if (polygon.length < 3) {
		hidePolygonFill(scene);
		return;
	}

	const shape = new THREE.Shape(polygon.map((point) => new THREE.Vector2(point.x, point.y)));
	const geometry = new THREE.ShapeGeometry(shape);
	geometry.translate(0, 0, polygon[0].z + 0.01);

	if (!polygonFillMesh) {
		const material = new THREE.MeshBasicMaterial({
			color: 0x00ff00,
			opacity: 0.2,
			transparent: true,
			side: THREE.DoubleSide,
			depthTest: false,
			depthWrite: false,
		});

		polygonFillMesh = new THREE.Mesh(geometry, material);
		polygonFillMesh.name = 'selectedGridFill';
		scene.add(polygonFillMesh);
	} else {
		polygonFillMesh.geometry.dispose();
		polygonFillMesh.geometry = geometry;
		polygonFillMesh.visible = true;
	}
};

export const hidePolygonFill = (_scene: THREE.Scene) => {
	if (!polygonFillMesh) {
		return;
	}
	polygonFillMesh.visible = false;
};

export const persistPolygonFill = (scene: THREE.Scene) => {
	if (polygonFillMesh) {
		const persistentMesh = polygonFillMesh.clone();
		if (Array.isArray(persistentMesh.material)) {
			persistentMesh.material.forEach((mat) => {
				if ('opacity' in mat) mat.opacity = 0.01;
			});
		} else if ('opacity' in persistentMesh.material) {
			persistentMesh.material.opacity = 0.01;
		}
		persistentMesh.name = 'selectedGridFill';
		addEdgesToMesh(persistentMesh, scene, 0x00ff00);
		scene.add(persistentMesh);
		persistedFillMeshes.push(persistentMesh);
	}

	polygonFillMesh?.geometry.dispose();
	if (polygonFillMesh) {
		if (Array.isArray(polygonFillMesh.material)) {
			polygonFillMesh.material.forEach((mat) => mat.dispose());
		} else {
			polygonFillMesh.material.dispose();
		}
	}
	polygonFillMesh = null;
};

export const highlightRoomFromData = (
	data: DailySunlightResult[],
	scene: THREE.Scene,
	isDetailed?: boolean
) => {
	const roomNames = new Set(data.map((d) => d.roomName));
	// Find the sunlight visualization instanced mesh
	let instancedMesh: THREE.InstancedMesh | null = null;
	scene.traverse((child) => {
		if (isDetailed) {
			if (
				child.userData.isSelectedRegionVisualization &&
				roomNames.has(child.userData.regionName) &&
				child instanceof THREE.InstancedMesh
			) {
				instancedMesh = child;
			}
		} else {
			if (
				child.userData.isRoomSunlightVisualization &&
				child instanceof THREE.InstancedMesh
			) {
				instancedMesh = child;
			}
		}
	});

	if (!instancedMesh) {
		console.warn('No sunlight visualization found in scene');
		return;
	}

	const mesh: THREE.InstancedMesh = instancedMesh;

	// Get the grid points data from the instanced mesh
	const gridPointsData = mesh.userData.gridPointsData as DailySunlightResult[];
	if (!gridPointsData || !Array.isArray(gridPointsData)) {
		console.warn('No grid points data found on instanced mesh');
		return;
	}

	// Remove any existing highlight mesh
	if (highlightedCellsMesh) {
		scene.remove(highlightedCellsMesh);
		highlightedCellsMesh.geometry.dispose();
		if (highlightedCellsMesh.material instanceof THREE.Material) {
			highlightedCellsMesh.material.dispose();
		}
		highlightedCellsMesh = null;
	}

	const highlightedCells: Array<{ matrix: THREE.Matrix4; index: number }> = [];
	const matrix = new THREE.Matrix4();
	const scale = new THREE.Vector3(1, 1, 1);

	gridPointsData.forEach((gridPoint, index) => {
		if (roomNames.has(gridPoint.roomName)) {
			mesh.getMatrixAt(index, matrix);
			const position = new THREE.Vector3();
			position.setFromMatrixPosition(matrix);

			const highlightMatrix = new THREE.Matrix4();
			highlightMatrix.compose(position, new THREE.Quaternion(), scale);

			highlightedCells.push({ matrix: highlightMatrix.clone(), index });
		}
	});

	if (highlightedCells.length === 0) {
		return;
	}

	// Create a separate instanced mesh for highlighted cells
	const cellSize = mesh.userData.gridSize || 1.0;
	const highlightGeometry = new THREE.PlaneGeometry(cellSize, cellSize);
	const highlightMaterial = new THREE.MeshBasicMaterial({
		color: 0x0088ff, // Cyan - stands out from building colors and sunlight gradient
		side: THREE.FrontSide,
		transparent: true,
		opacity: 0.6,
		depthWrite: false,
		depthTest: false,
	});

	highlightedCellsMesh = new THREE.InstancedMesh(
		highlightGeometry,
		highlightMaterial,
		highlightedCells.length
	);
	highlightedCellsMesh.name = 'HighlightedCells';
	highlightedCellsMesh.userData.isHighlightedCells = true;
	highlightedCellsMesh.renderOrder = 10000;

	// Set matrices for highlighted cells
	highlightedCells.forEach((cell, index) => {
		highlightedCellsMesh!.setMatrixAt(index, cell.matrix);
	});

	highlightedCellsMesh.instanceMatrix.needsUpdate = true;
	scene.add(highlightedCellsMesh);
};

export const removeHighlightFromData = (data: DailySunlightResult[], scene: THREE.Scene) => {
	// Remove the highlighted cells mesh
	if (highlightedCellsMesh) {
		scene.remove(highlightedCellsMesh);
		highlightedCellsMesh.geometry.dispose();
		if (highlightedCellsMesh.material instanceof THREE.Material) {
			highlightedCellsMesh.material.dispose();
		}
		highlightedCellsMesh = null;
	}
};

export const clearAllVisualizations = (scene: THREE.Scene) => {
	clearSunlightVisualization(scene);
	clearSelectedRegionVisualization(scene);
	hidePolygonOutline(scene);
	clearAllSelections(scene);
};
